import React from 'react';

import { isDefined } from '../../../../utils/variables';
import { AssetManager } from '../../../../viewer/asset-manager';
import { AssetCategory, ElementObj } from '../../../../viewer/elements/element-obj';
import { publish, SubscriptionTopic } from '../../../../viewer/messaging/pubsub';
import { useSubscribe } from '../../../hooks/use-subscribe';

interface CheckBoxContext {
  checkAll: (uuids: string[]) => void;
  checkedAssetUuids: string[];
  toggle: (uuids: string) => void;
  uncheckAll: (uuids: string[]) => void;
  visible: boolean;
  disabled: boolean;
}

const CheckBoxContext = React.createContext<CheckBoxContext>(undefined!);

type Props = {
  assets: ElementObj[];
  children: React.ReactNode;
  categoryType: AssetCategory;
};

const CheckBoxState = (props: Props) => {
  const [visible, setVisible] = React.useState(false);
  const [disabled, setDisabled] = React.useState(false);
  const [checkedAssetUuids, setCheckedAssetUuids] = React.useState<string[]>(['']);

  useSubscribe(SubscriptionTopic.ShowAssetMenuCheckBoxes, (message) => {
    /**
     * Returns a list of asset UUID to check. If undefined is returned, current checked assets
     * should not be changed.
     */
    const resolveAssetsToCheck = () => {
      if (!isDefined(message.autoCheckVisibleAssets) && !isDefined(message.autoCheckAssetIds)) {
        return undefined;
      }

      const assetsToCheck = new Set<string>();

      if (message.autoCheckVisibleAssets) {
        props.assets.filter((x) => x.visible).forEach((x) => assetsToCheck.add(x.uuid));
      }

      if (message.autoCheckAssetIds) {
        message.autoCheckAssetIds.forEach((assetId) => {
          const asset = AssetManager.instance.getAssetByAssetId(assetId);
          if (asset) {
            assetsToCheck.add(asset.uuid);
          }
        });
      }

      return [...assetsToCheck];
    };

    if (message.categories.includes(props.categoryType)) {
      // Only change checked assets if specified an array of IDs (or empty - reset checkboxes).
      const assetsToCheck = resolveAssetsToCheck();
      if (isDefined(assetsToCheck)) {
        setCheckedAssetUuids(assetsToCheck);
      }

      setVisible(true);
      setDisabled(isDefined(message.disableAll) && message.disableAll);
    }
  });

  useSubscribe(SubscriptionTopic.HideAssetMenuCheckBoxes, () => {
    setVisible(false);
  });

  React.useEffect(() => {
    publish(SubscriptionTopic.AssetMenuCategoryCheckBoxesChanged, {
      category: props.categoryType,
      checkedAssetUuids,
    });
  }, [checkedAssetUuids, props.categoryType]);

  return (
    <CheckBoxContext.Provider
      value={{
        visible,
        disabled,
        checkedAssetUuids,
        toggle: React.useCallback(
          (uuid) => {
            if (checkedAssetUuids.includes(uuid)) {
              setCheckedAssetUuids(checkedAssetUuids.filter((x) => x !== uuid));
            } else {
              setCheckedAssetUuids([...checkedAssetUuids, uuid]);
            }
          },
          [checkedAssetUuids],
        ),
        checkAll: React.useCallback(
          (assetUuids: string[]) => {
            const uniqueUuids = [...new Set([...checkedAssetUuids, ...assetUuids])];
            setCheckedAssetUuids(uniqueUuids);
          },
          [checkedAssetUuids],
        ),
        uncheckAll: React.useCallback(
          (assetUuids: string[]) => {
            setCheckedAssetUuids(checkedAssetUuids.filter((x) => !assetUuids.includes(x)));
          },
          [checkedAssetUuids],
        ),
      }}
    >
      {props.children}
    </CheckBoxContext.Provider>
  );
};

export { CheckBoxContext, CheckBoxState };
